/**
 * frontend/src/pages/CheckIn.jsx
 * ================================
 * Staff Check-In PWA — mobile-first, saved to homescreen.
 *
 * AUTH: Token in URL query param (?token=...).
 * No login form — staff get this URL from the manager and save it
 * to their phone homescreen via "Add to Home Screen".
 *
 * The token is read from the URL and sent on every submission.
 * It is stored in localStorage (fine here — /checkin is not in an iframe).
 *
 * Flow:
 *   1. Staff open URL with token: ridgeline.chekn.io/checkin?token=abc123
 *   2. Page validates token against partner list (GET /api/partners?token=abc123
 *      is not exposed — instead, the submit will 401 if the token is bad)
 *   3. Staff selects partner, enters guest count, submits
 *   4. Success screen with haptic feedback cue
 */

import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { checkinsApi, partnersApi } from '../lib/api'

// Staff submit hits /api/checkins/submit?token=...
// Partners list is fetched without auth (we do it from the token-protected
// submit endpoint implicitly — but we need the list for the UI).
// Actually, we do need the partners list. We'll fetch it using the token
// as a query param on the backend (add a public partner list endpoint).
// For now: fetch with the staff token passed as X-Staff-Token header trick.
// Simplest approach: fetch partners via regular API using the token as auth.

const TOKEN_STORAGE_KEY = 'chekn_checkin_token'

function useStaffToken() {
  const [token, setToken] = useState(() => {
    // Prefer URL param (fresh link), fall back to stored value
    const params = new URLSearchParams(window.location.search)
    const urlToken = params.get('token')
    if (urlToken) {
      localStorage.setItem(TOKEN_STORAGE_KEY, urlToken)
      return urlToken
    }
    return localStorage.getItem(TOKEN_STORAGE_KEY)
  })
  return token
}

// ─── Success screen ───────────────────────────────────────────────────────

function SuccessScreen({ partnerName, guestCount, onReset }) {
  useEffect(() => {
    // Haptic feedback on supported devices
    if ('vibrate' in navigator) navigator.vibrate([100, 50, 100])
  }, [])

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-950 px-6 text-center">
      <div className="text-7xl mb-6 animate-bounce">✅</div>
      <h2 className="text-2xl font-bold text-white mb-2">Check-In Recorded!</h2>
      <p className="text-gray-400 mb-1">
        <span className="text-white font-semibold">{guestCount}</span>{' '}
        {guestCount === 1 ? 'guest' : 'guests'} from
      </p>
      <p className="text-violet-400 font-semibold text-lg mb-8">{partnerName}</p>
      <button
        onClick={onReset}
        className="w-full max-w-xs bg-violet-700 hover:bg-violet-600 text-white font-semibold py-4 rounded-2xl text-lg transition-colors active:scale-95"
      >
        New Check-In
      </button>
    </div>
  )
}

// ─── No token screen ──────────────────────────────────────────────────────

function NoTokenScreen() {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-950 px-8 text-center">
      <div className="text-5xl mb-5">🔗</div>
      <h2 className="text-xl font-bold text-white mb-3">Link Required</h2>
      <p className="text-gray-400 text-sm leading-relaxed">
        This page requires a check-in link from your winery manager.
        Please contact your manager for the correct URL.
      </p>
    </div>
  )
}

// ─── Main Check-In form ───────────────────────────────────────────────────

export default function CheckIn() {
  const token = useStaffToken()
  const [selectedPartner, setSelectedPartner] = useState(null)
  const [guestCount, setGuestCount] = useState(2)
  const [notes, setNotes] = useState('')
  const [success, setSuccess] = useState(null)
  const [error, setError] = useState(null)

  // Fetch partners list — uses ?token= so backend validates access
  const { data: partners = [], isLoading, isError } = useQuery({
    queryKey: ['checkin-partners', token],
    queryFn: async () => {
      if (!token) return []
      // We use the public partners list; backend will validate the token on submit
      const res = await partnersApi.list({ active_only: true })
      return res.data
    },
    enabled: !!token,
    retry: 1,
  })

  const submitMutation = useMutation({
    mutationFn: (data) => checkinsApi.submit(token, data),
    onSuccess: (res, variables) => {
      const partner = partners.find((p) => p.id === variables.partner_id)
      setSuccess({ partnerName: partner?.name ?? '—', guestCount: variables.guest_count })
      setError(null)
    },
    onError: (err) => {
      const msg = err.response?.data?.detail
      if (err.response?.status === 401) {
        setError('Your check-in token is invalid or expired. Contact your manager.')
      } else {
        setError(msg || 'Submission failed. Check your connection and try again.')
      }
    },
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!selectedPartner) return
    setError(null)
    submitMutation.mutate({
      partner_id: selectedPartner.id,
      guest_count: guestCount,
      notes: notes.trim() || undefined,
    })
  }

  const handleReset = () => {
    setSuccess(null)
    setSelectedPartner(null)
    setGuestCount(2)
    setNotes('')
  }

  if (!token) return <NoTokenScreen />
  if (success) return <SuccessScreen {...success} onReset={handleReset} />

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col">
      {/* Header */}
      <div className="px-5 pt-safe pt-8 pb-4 border-b border-gray-800">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-2xl">🍷</span>
          <span className="font-bold text-white text-lg">Chekn</span>
        </div>
        <p className="text-sm text-gray-500">Staff Check-In</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="flex-1 px-5 py-6 space-y-6">
        {error && (
          <div className="bg-red-900/40 border border-red-800 text-red-300 text-sm rounded-xl px-4 py-3">
            {error}
          </div>
        )}

        {/* Partner selection */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-3">
            Which partner sent this group?
          </label>

          {isLoading && (
            <div className="space-y-2">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-14 bg-gray-800 rounded-xl animate-pulse" />
              ))}
            </div>
          )}

          {isError && (
            <p className="text-red-400 text-sm">
              Failed to load partners. Check your connection.
            </p>
          )}

          <div className="space-y-2">
            {partners.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setSelectedPartner(p)}
                className={`w-full text-left px-4 py-3.5 rounded-xl border transition-all ${
                  selectedPartner?.id === p.id
                    ? 'bg-violet-700 border-violet-500 text-white'
                    : 'bg-gray-900 border-gray-800 text-gray-300 active:bg-gray-800'
                }`}
              >
                <div className="font-medium">{p.name}</div>
                <div className="text-xs opacity-70 capitalize">{p.type}</div>
              </button>
            ))}
          </div>

          {!isLoading && partners.length === 0 && (
            <p className="text-gray-500 text-sm text-center py-6">
              No active partners. Ask your manager to add partners first.
            </p>
          )}
        </div>

        {/* Guest count */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-3">
            Number of guests
          </label>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => setGuestCount((n) => Math.max(1, n - 1))}
              className="w-12 h-12 bg-gray-800 hover:bg-gray-700 text-white text-2xl rounded-xl flex items-center justify-center active:scale-95 transition-all"
            >
              −
            </button>
            <span className="text-4xl font-bold text-white tabular-nums w-16 text-center">
              {guestCount}
            </span>
            <button
              type="button"
              onClick={() => setGuestCount((n) => Math.min(100, n + 1))}
              className="w-12 h-12 bg-gray-800 hover:bg-gray-700 text-white text-2xl rounded-xl flex items-center justify-center active:scale-95 transition-all"
            >
              +
            </button>
          </div>
        </div>

        {/* Notes (optional) */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Notes{' '}
            <span className="text-gray-500 font-normal text-xs">(optional)</span>
          </label>
          <textarea
            rows={2}
            className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-violet-500 resize-none"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. VIP group, wine club members…"
          />
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={!selectedPartner || submitMutation.isPending}
          className="w-full bg-violet-700 hover:bg-violet-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold py-4 rounded-2xl text-lg transition-all active:scale-[0.98]"
        >
          {submitMutation.isPending ? 'Saving…' : 'Submit Check-In'}
        </button>
      </form>
    </div>
  )
}
